import { WsListenerClient } from './ws-listener';
import { CaptionRenderer } from './caption-renderer';
import { ScreenWakeLock } from './screen-wake-lock';
import { setupChannelBadge } from '../channel-badge';
import { initTheme, toggleTheme, getCurrentTheme, onThemeChange, AppTheme } from '../theme-manager';
import { t } from '../i18n';
import { isRtlLanguage } from '../core/rtl';
import {
  CLASS_HIDDEN,
  EVENT_CLICK,
  EVENT_CHANGE,
  EVENT_BEFOREUNLOAD,
  EVENT_PAGEHIDE,
  PARAM_LANG,
  STORAGE_VIEWER_LANGUAGE,
} from '../core/strings';

export class ListenerView {
  private container: HTMLElement;
  private channelName: string;
  private currentLanguage: string = 'source';
  private wsClient: WsListenerClient;
  private renderer: CaptionRenderer | null = null;
  private wakeLock: ScreenWakeLock;
  private themeUnsubscribe: (() => void) | null = null;

  private currentFontSize: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  private autoScrollEnabled: boolean = true;

  constructor(container: HTMLElement, channelName: string, initialLanguage?: string) {
    this.container = container;
    this.channelName = channelName;
    if (initialLanguage) {
      this.currentLanguage = initialLanguage;
    }
    this.wakeLock = new ScreenWakeLock();

    // Initialize system/desktop-aware theme
    const theme = initTheme();

    this.render();

    const feedEl = this.container.querySelector('#caption-feed') as HTMLElement;
    const resumeBtn = this.container.querySelector('#resume-scroll-btn') as HTMLElement;

    this.renderer = new CaptionRenderer(
      {
        container: feedEl,
        fontSize: this.currentFontSize,
        theme: theme,
        autoScroll: this.autoScrollEnabled,
        language: this.currentLanguage,
      },
      (isScrolledUp) => {
        if (resumeBtn) {
          resumeBtn.classList.toggle(CLASS_HIDDEN, !isScrolledUp);
        }
      }
    );
    this.updateLanguageDirection(this.currentLanguage);

    this.themeUnsubscribe = onThemeChange((newTheme) => {
      this.updateThemeButtonLabel(newTheme);
    });

    this.wsClient = new WsListenerClient({
      onCaption: (caption) => {
        this.renderer?.handleCaption(caption);
        this.wakeLock.pokeActivity();
      },
      onStatusChange: (status, detail) => {
        this.updateStatus(status, detail);
        if (status === 'connected') {
          this.wakeLock.requestLock();
        } else if (status === 'disconnected') {
          this.wakeLock.releaseLock();
        }
      },
    });

    this.bindEvents();
    this.wsClient.connect(this.channelName, this.currentLanguage);
    this.wakeLock.requestLock();

    const select = this.container.querySelector('#listener-language-select') as HTMLSelectElement | null;
    if (select && this.currentLanguage && this.currentLanguage !== 'source') {
      select.value = this.currentLanguage;
      this.updateLanguageDirection(this.currentLanguage);
      this.renderer?.setLanguage(this.currentLanguage);
    }
  }

  private updateThemeButtonLabel(theme: AppTheme): void {
    const themeBtn = this.container.querySelector('#theme-toggle-btn');
    if (themeBtn) {
      const icon = theme === 'dark' ? '🌙' : theme === 'light' ? '☀️' : '⚡';
      const label = theme.charAt(0).toUpperCase() + theme.slice(1);
      const iconEl = themeBtn.querySelector('.theme-btn-icon');
      const labelEl = themeBtn.querySelector('.theme-btn-label');
      if (iconEl) iconEl.textContent = icon;
      if (labelEl) labelEl.textContent = label;
    }
  }

  private render(): void {
    const currentTheme = getCurrentTheme();
    this.updateThemeButtonLabel(currentTheme);

    const badgeEl = this.container.querySelector('#channel-badge') as HTMLElement | null;
    if (badgeEl) {
      setupChannelBadge(badgeEl, {
        channel: this.channelName,
        showLogo: true,
      });
    }
  }

  private bindEvents(): void {
    const fontDecrease = this.container.querySelector('#font-decrease-btn');
    const fontIncrease = this.container.querySelector('#font-increase-btn');
    const feed = this.container.querySelector('#caption-feed');
    const themeBtn = this.container.querySelector('#theme-toggle-btn');
    const resumeBtn = this.container.querySelector('#resume-scroll-btn');

    const fontSizes: Array<'sm' | 'md' | 'lg' | 'xl'> = ['sm', 'md', 'lg', 'xl'];

    fontDecrease?.addEventListener(EVENT_CLICK, () => {
      const idx = fontSizes.indexOf(this.currentFontSize);
      if (idx > 0) {
        this.currentFontSize = fontSizes[idx - 1];
        if (feed) {
          feed.className = `caption-feed font-${this.currentFontSize}`;
        }
      }
    });

    fontIncrease?.addEventListener(EVENT_CLICK, () => {
      const idx = fontSizes.indexOf(this.currentFontSize);
      if (idx < fontSizes.length - 1) {
        this.currentFontSize = fontSizes[idx + 1];
        if (feed) {
          feed.className = `caption-feed font-${this.currentFontSize}`;
        }
      }
    });

    themeBtn?.addEventListener(EVENT_CLICK, () => {
      toggleTheme();
    });

    resumeBtn?.addEventListener(EVENT_CLICK, () => {
      this.renderer?.scrollToBottom(true);
    });

    const langSelect = this.container.querySelector('#listener-language-select') as HTMLSelectElement | null;
    langSelect?.addEventListener(EVENT_CHANGE, () => {
      const selected = langSelect.value || 'source';
      this.currentLanguage = selected;
      this.updateLanguageDirection(selected);
      this.renderer?.setLanguage(selected);
      this.wsClient.setLanguage(selected);

      try {
        if (selected && selected !== 'source') {
          localStorage.setItem(STORAGE_VIEWER_LANGUAGE, selected);
        } else {
          localStorage.removeItem(STORAGE_VIEWER_LANGUAGE);
        }
      } catch {
        // Ignore in restricted environments
      }

      try {
        const url = new URL(window.location.href);
        if (selected && selected !== 'source') {
          url.searchParams.set(PARAM_LANG, selected);
        } else {
          url.searchParams.delete(PARAM_LANG);
        }
        window.history.replaceState(null, '', url.toString());
      } catch {
        // Ignore URL manipulation error in non-standard environments
      }
    });

    window.addEventListener(EVENT_BEFOREUNLOAD, () => {
      this.destroy();
    });
    window.addEventListener(EVENT_PAGEHIDE, () => {
      this.destroy();
    });
  }

  private updateLanguageDirection(lang: string): void {
    const isRtl = isRtlLanguage(lang);
    const feed = this.container.querySelector('#caption-feed');
    if (feed) {
      if (isRtl) {
        feed.classList.add('is-rtl');
        feed.setAttribute('dir', 'rtl');
      } else {
        feed.classList.remove('is-rtl');
        feed.setAttribute('dir', 'ltr');
      }
    }
  }

  private updateStatus(status: 'connected' | 'reconnecting' | 'disconnected', detail?: string): void {
    const pill = this.container.querySelector('#listener-status-pill');
    const txt = this.container.querySelector('#listener-status-text');
    if (!pill || !txt) return;

    pill.className = `status-pill ${status}`;
    if (status === 'connected') {
      txt.textContent = t('listener.live');
    } else if (status === 'reconnecting') {
      txt.textContent = detail || t('listener.connecting');
    } else {
      txt.textContent = detail || t('listener.disconnected');
    }
  }

  destroy(): void {
    if (this.themeUnsubscribe) {
      this.themeUnsubscribe();
      this.themeUnsubscribe = null;
    }
    this.wakeLock.destroy();
    this.wsClient.disconnect();
  }
}
